import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { normalizePhoneParts, toE164 } from '../../utils/phone';

// `value` = ulusal numara (ülke kodu YOK), `dialCode` = ülke kodu.
//
// react-international-phone E.164 ile çalışır, bu yüzden gösterilecek değer
// toE164 ile birleştirilir; onChange'de ise ülke kodu numaradan AYRILIP çağırana
// iki ayrı parça olarak verilir. Eskiden bileşen E.164 değerini olduğu gibi
// veriyordu ve ülke kodu ayrıca areaCode alanına da yazılıyordu — gösterimde
// "+90 +905558733768" çiftlenmesi buradan doğuyordu.
//
// Ayrıca eski hâlde `value` ulusal numara olduğunda ("5558733768") bileşen ülkeyi
// TAHMİN ediyordu ve numarayı Brezilya (+55) sanıyordu; kaydedince ülke kodu
// bozuluyordu. dialCode'u vererek tahmini ortadan kaldırıyoruz.
const PhoneNumberInput = ({
    value,
    dialCode,
    onChange,
    label = "Telefon",
    required = false,
    description = "",
    error,
    defaultCountry = "tr",
}) => {
    const [localValue, setLocalValue] = useState(() => toE164(dialCode, value));
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        setLocalValue(toE164(dialCode, value));
    }, [value, dialCode]);

    const handlePhoneChange = (newValue, meta) => {
        setLocalValue(newValue);
        const code = meta?.country?.dialCode || dialCode;
        const parts = normalizePhoneParts(code, newValue);
        onChange?.(parts.phoneNumber, parts.areaCode);
    };

    return (
        <OuterWrapper $isFocused={isFocused} $error={!!error}>
            <FixedLabel $isFocused={isFocused} $error={!!error}>
                {label}
                {required && <Asterisk>*</Asterisk>}
            </FixedLabel>

            <PhoneInput
                defaultCountry={defaultCountry}
                value={localValue}
                onChange={handlePhoneChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                inputClassName="phone-input-field"
                containerClassName="phone-input-container"
                countrySelectorStyleProps={{
                    buttonClassName: 'country-selector-button',
                    dropdownClassName: 'country-selector-dropdown',
                }}
            />
            {error && <ErrorText>{error}</ErrorText>}
            {description && <DescriptionText>{description}</DescriptionText>}
        </OuterWrapper>
    );
};

export default PhoneNumberInput;

const OuterWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: 1;

  &:focus-within { z-index: 9999; }
  &:hover { z-index: 9999; }

  .phone-input-container {
    width: 100%;
    height: 48px;
    display: flex;
    align-items: center;
  }

  .phone-input-field {
    width: 100% !important;
    height: 48px !important;
    border-radius: 0 4px 4px 0 !important;
    border: 1.5px solid ${p => p.$error ? '#E53935' : p.$isFocused ? '#0291EF' : '#E0E4E8'} !important;
    border-left: none !important;
    font-size: 14px !important;
    font-family: 'Nunito Sans', sans-serif !important;
    color: #004242 !important;
    transition: all 0.2s ease !important;
    box-shadow: ${p => p.$isFocused ? '0 0 0 3px rgba(2, 145, 239, 0.1)' : 'none'} !important;
    &:focus { outline: none !important; }
  }

  .country-selector-button {
    height: 48px !important;
    border-radius: 4px 0 0 4px !important;
    border: 1.5px solid ${p => p.$error ? '#E53935' : p.$isFocused ? '#0291EF' : '#E0E4E8'} !important;
    border-right: none !important;
    background-color: #fff !important;
    padding: 0 10px !important;
    transition: all 0.2s ease !important;
    box-shadow: ${p => p.$isFocused ? '0 0 0 3px rgba(2, 145, 239, 0.08)' : 'none'} !important;
    clip-path: inset(-10px 0px -10px -10px);
    &:hover { background-color: #F5F7FA !important; }
  }

  .country-selector-dropdown {
    z-index: 10000 !important;
    background-color: #fff !important;
    border-radius: 6px !important;
    border: 1px solid #E8ECF0 !important;
    box-shadow: 0 8px 24px rgba(0, 66, 66, 0.1) !important;
    margin-top: 4px !important;
    font-family: 'Nunito Sans', sans-serif !important;

    .react-international-phone-country-selector-dropdown__list-item {
      padding: 8px 12px !important;
      font-size: 13px !important;
      &:hover { background-color: #F5F7FA !important; }
    }
  }

  &:focus-within .fixed-label { color: #0291EF; }
`;

const FixedLabel = styled.label.attrs({ className: 'fixed-label' })`
  position: absolute;
  top: -8px;
  left: 12px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'Nunito Sans', sans-serif;
  padding: 0 4px;
  z-index: 2;
  background-color: #fff;
  pointer-events: none;
  color: ${p => p.$error ? '#E53935' : p.$isFocused ? '#0291EF' : '#A0A8B4'};
  transition: color 0.2s ease;
`;

const Asterisk = styled.span`
  color: #E53935;
  margin-left: 2px;
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #E53935;
`;

const DescriptionText = styled.span`
  display: block;
  margin-top: 4px;
  color: #A0A8B4;
  font-size: 11px;
`;
